/**
 * Business Unit Helper
 * 
 * Provides helper functions for business unit operations with caching.
 * Assumes: 1 business_unit per workspace (UNIQUE workspace_id constraint).
 */

import { supabase } from '@/integrations/supabase/client';

// In-memory cache for business_unit_id lookups
const businessUnitCache = new Map<string, { id: string; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get default business_unit_id for a workspace
 * Uses caching to avoid repeated queries.
 * 
 * @param workspaceId - The workspace ID
 * @returns Promise<string | null> - The business_unit_id, or null if not found
 */
export async function getDefaultBusinessUnitId(workspaceId: string): Promise<string | null> {
  if (!workspaceId) return null;

  // Check cache first
  const cached = businessUnitCache.get(workspaceId);
  const now = Date.now();
  if (cached && (now - cached.timestamp) < CACHE_TTL) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[getDefaultBusinessUnitId] Using cached business_unit_id:', cached.id, 'for workspace:', workspaceId);
    }
    return cached.id;
  }

  // Fetch from database
  try {
    const { data, error } = await supabase
      .from('business_units')
      .select('id')
      .eq('workspace_id', workspaceId)
      .maybeSingle();

    if (error) {
      console.error('[getDefaultBusinessUnitId] Error fetching business_unit:', error);
      return null;
    }

    if (!data) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[getDefaultBusinessUnitId] No business_unit found for workspace:', workspaceId);
      }
      return null;
    }

    // Cache the result
    businessUnitCache.set(workspaceId, { id: data.id, timestamp: now });
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[getDefaultBusinessUnitId] Cached business_unit_id:', data.id, 'for workspace:', workspaceId);
    }

    return data.id;
  } catch (err) {
    console.error('[getDefaultBusinessUnitId] Unexpected error:', err);
    return null;
  }
}

/**
 * Clear the business unit cache for a specific workspace
 * Call this when business units are created/updated/deleted
 */
export function clearBusinessUnitCache(workspaceId?: string) {
  if (workspaceId) {
    businessUnitCache.delete(workspaceId);
  } else {
    businessUnitCache.clear();
  }
}

